// src/pages/LandingPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  apiLogin, apiRegister, apiRegisterVerify, apiRegisterResend,
  apiLoginRestoreConfirm, apiForgotPasswordRequest, apiForgotPasswordConfirm,
} from '../services/api';
import OtpInput from '../components/OtpInput';
import { Link } from 'react-router-dom';
import styles from './LandingPage.module.css';

function PasswordInput({ id, styles, ...props }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={styles.passwordWrap}>
      <input id={id} className={styles.input} type={visible ? 'text' : 'password'} {...props} />
      <button
        type="button"
        className={styles.eyeBtn}
        onClick={() => setVisible(v => !v)}
        tabIndex={-1}
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? (
          <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
            <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.4"/>
            <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.4"/>
          </svg>
        ) : (
          <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
            <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.4"/>
            <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.4"/>
            <line x1="3" y1="17" x2="17" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        )}
      </button>
    </div>
  );
}

// ── Auth Modal ────────────────────────────────────────────────────────────────
function AuthModal({ onClose }) {
  const { login } = useAuth();
  const [step, setStep]       = useState('login'); // login | signup | signup-otp | restore-confirm | forgot-email | forgot-otp | forgot-done
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const [loginData, setLoginData] = useState({ username: '', password: '', keepLoggedIn: false });
  const [signupData, setSignupData] = useState({ fullName: '', email: '', username: '', password: '' });

  const [pendingId, setPendingId]   = useState(null);
  const [otp, setOtp]               = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const [restoreToken, setRestoreToken] = useState(null);

  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotEmail, setForgotEmail]        = useState('');
  const [forgotNewPassword, setForgotNewPassword]         = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [doneCountdown, setDoneCountdown] = useState(5);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Auto-return to login after password reset succeeds
  useEffect(() => {
    if (step !== 'forgot-done') return;
    if (doneCountdown <= 0) { switchTab('login'); return; }
    const t = setTimeout(() => setDoneCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [step, doneCountdown]);

  const switchTab = (t) => {
    setStep(t);
    setError('');
    setOtp('');
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.username || !loginData.password) { setError('Please fill in all fields.'); return; }
    setLoading(true); setError('');
    try {
      const data = await apiLogin(loginData.username, loginData.password);
      if (data.status === 'restore_available') {
        setRestoreToken(data.restore_token);
        setStep('restore-confirm');
      } else {
        login(data.user, data.token, loginData.keepLoggedIn);
      }
    } catch (err) {
      let msg = err.message || 'Login failed. Please try again.';
      if (err.locked && err.retryAfterSeconds) {
        const mins = Math.ceil(err.retryAfterSeconds / 60);
        msg = `${msg} Try again in ~${mins} min.`;
      } else if (typeof err.attemptsLeft === 'number') {
        msg = `${msg} (${err.attemptsLeft} attempt${err.attemptsLeft === 1 ? '' : 's'} left)`;
      }
      setError(msg);
    } finally { setLoading(false); }
  };

  // ── Restore confirm ───────────────────────────────────────────────────────
  const handleRestoreConfirm = async (confirm) => {
    setLoading(true); setError('');
    try {
      const data = await apiLoginRestoreConfirm(restoreToken, confirm);
      if (confirm && data.status === 'success') {
        login(data.user, data.token, loginData.keepLoggedIn);
      } else {
        setStep('login');
        setError('Your account was not restored.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
      setStep('login');
    } finally { setLoading(false); }
  };

  // ── Signup: step 1 (submit form → OTP sent) ──────────────────────────────
  const handleSignup = async (e) => {
    e.preventDefault();
    const { fullName, email, username, password } = signupData;
    if (!fullName || !email || !username || !password) { setError('Please fill in all fields.'); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email address.'); return; }
    setLoading(true); setError('');
    try {
      const data = await apiRegister({ full_name: fullName, email, username, password });
      setPendingId(data.pending_id);
      setOtp('');
      setResendCooldown(60);
      setStep('signup-otp');
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally { setLoading(false); }
  };

  // ── Signup: step 2 (verify OTP) ───────────────────────────────────────────
  const handleSignupVerify = async () => {
    if (otp.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      const data = await apiRegisterVerify(pendingId, otp);
      login(data.user, data.token, false);
    } catch (err) {
      let msg = err.message || 'Verification failed.';
      if (!err.locked && typeof err.attemptsLeft === 'number') msg = `${msg} (${err.attemptsLeft} attempt${err.attemptsLeft === 1 ? '' : 's'} left)`;
      else if (typeof err.attemptsLeft === 'number') msg = `${msg} (${err.attemptsLeft} attempt${err.attemptsLeft === 1 ? '' : 's'} left)`;
      setError(msg);
      setOtp('');
    } finally { setLoading(false); }
  };

  const handleSignupResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await apiRegisterResend(pendingId);
      setResendCooldown(60);
      setOtp('');
    } catch (err) {
      setError(err.message || 'Could not resend code.');
      if (err.waitSeconds) setResendCooldown(err.waitSeconds);
    }
  };

  // ── Forgot password: step 1 (username + email → OTP sent) ────────────────
  const handleForgotRequest = async (e) => {
    e.preventDefault();
    if (!forgotUsername || !forgotEmail) { setError('Please fill in both fields.'); return; }
    setLoading(true); setError('');
    try {
      await apiForgotPasswordRequest(forgotEmail, forgotUsername);
      setOtp('');
      setStep('forgot-otp');
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally { setLoading(false); }
  };

  // ── Forgot password: step 2 (OTP + new password) ─────────────────────────
  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    if (!forgotNewPassword || !forgotConfirmPassword) { setError('Please fill in both password fields.'); return; }
    if (forgotNewPassword !== forgotConfirmPassword) { setError('Passwords do not match.'); return; }
    if (forgotNewPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await apiForgotPasswordConfirm(forgotEmail, otp, forgotNewPassword, forgotConfirmPassword);
      setDoneCountdown(5);
      setStep('forgot-done');
    } catch (err) {
      let msg = err.message || 'Reset failed.';
      if (!err.locked && typeof err.attemptsLeft === 'number') msg = `${msg} (${err.attemptsLeft} attempt${err.attemptsLeft === 1 ? '' : 's'} left)`;
      else if (typeof err.attemptsLeft === 'number') msg = `${msg} (${err.attemptsLeft} attempt${err.attemptsLeft === 1 ? '' : 's'} left)`;
      setError(msg);
      setOtp('');
    } finally { setLoading(false); }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalCard} onClick={e => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose} type="button" aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="1" x2="13" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="13" y1="1" x2="1" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        <div className={styles.modalLogo}>
          <span className={styles.modalLogoIcon}>D</span>
          <span className={styles.modalLogoText}>Delefy</span>
        </div>

        {/* Tabs only show on the two primary entry screens */}
        {(step === 'login' || step === 'signup') && (
          <div className={styles.tabs}>
            <button className={`${styles.tab} ${step === 'login' ? styles.tabActive : ''}`} onClick={() => switchTab('login')} type="button">Log In</button>
            <button className={`${styles.tab} ${step === 'signup' ? styles.tabActive : ''}`} onClick={() => switchTab('signup')} type="button">Sign Up</button>
          </div>
        )}

        {error && <div className={styles.errorMsg}>{error}</div>}

        {/* ── LOGIN ── */}
        {step === 'login' && (
          <form className={styles.form} onSubmit={handleLogin} noValidate>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="login-username">Username</label>
              <input id="login-username" className={styles.input} type="text" placeholder="Enter your username"
                autoComplete="username" value={loginData.username}
                onChange={e => setLoginData(p => ({ ...p, username: e.target.value.toLowerCase() }))} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="login-password">Password</label>
              <PasswordInput id="login-password" styles={styles} placeholder="Enter your password"
                autoComplete="current-password" value={loginData.password}
                onChange={e => setLoginData(p => ({ ...p, password: e.target.value }))} />
            </div>
            <div className={styles.checkboxRow}>
              <label className={styles.checkboxLabel} htmlFor="keep-logged-in">
                <input id="keep-logged-in" type="checkbox" className={styles.checkbox}
                  checked={loginData.keepLoggedIn}
                  onChange={e => setLoginData(p => ({ ...p, keepLoggedIn: e.target.checked }))} />
                <span className={styles.checkboxCustom} />
                Keep me logged in
              </label>
            </div>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              {loading ? <span className={styles.spinner} /> : 'Log In'}
            </button>
            <p className={styles.switchText}>
              <button type="button" className={styles.switchLink} onClick={() => { setForgotUsername(''); setForgotEmail(''); setForgotNewPassword(''); setForgotConfirmPassword(''); switchTab('forgot-email'); }}>
                Forgot password?
              </button>
            </p>
            <p className={styles.switchText}>
              Don't have an account?{' '}
              <button type="button" className={styles.switchLink} onClick={() => switchTab('signup')}>Sign up</button>
            </p>
          </form>
        )}

        {/* ── SIGNUP ── */}
        {step === 'signup' && (
          <form className={styles.form} onSubmit={handleSignup} noValidate>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="signup-name">Full Name</label>
              <input id="signup-name" className={styles.input} type="text" placeholder="Enter your full name"
                autoComplete="name" value={signupData.fullName}
                onChange={e => setSignupData(p => ({ ...p, fullName: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="signup-email">Email ID</label>
              <input id="signup-email" className={styles.input} type="email" placeholder="Enter your email address"
                autoComplete="email" value={signupData.email}
                onChange={e => setSignupData(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="signup-username">Username</label>
              <input id="signup-username" className={styles.input} type="text" placeholder="Choose a username"
                autoComplete="username" maxLength={20} value={signupData.username}
                onChange={e => setSignupData(p => ({ ...p, username: e.target.value.toLowerCase() }))} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="signup-password">Password</label>
              <PasswordInput id="signup-password" styles={styles} placeholder="Create a strong password"
                autoComplete="new-password" value={signupData.password}
                onChange={e => setSignupData(p => ({ ...p, password: e.target.value }))} />
            </div>
            <p className={styles.consentText}>
              By clicking Create Account, you agree to our{' '}
              <Link to="/legal" target="_blank" className={styles.consentLink}>Terms & Privacy.</Link>
            </p>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              {loading ? <span className={styles.spinner} /> : 'Create Account'}
            </button>
            <p className={styles.switchText}>
              Already have an account?{' '}
              <button type="button" className={styles.switchLink} onClick={() => switchTab('login')}>Log in</button>
            </p>
          </form>
        )}

        {/* ── SIGNUP OTP ── */}
        {step === 'signup-otp' && (
          <div className={styles.form}>
            <p className={styles.switchText} style={{ marginBottom: '0.5rem' }}>
              We need to verify your email address. An OTP has been sent to <strong>{signupData.email}</strong>. Please enter it below.
            </p>
            <OtpInput value={otp} onChange={setOtp} disabled={loading} />
            <button type="button" className={styles.primaryBtn} disabled={loading} onClick={handleSignupVerify}>
              {loading ? <span className={styles.spinner} /> : 'Verify'}
            </button>
            <p className={styles.switchText}>
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : <button type="button" className={styles.switchLink} onClick={handleSignupResend}>Resend code</button>
              }
            </p>
          </div>
        )}

        {/* ── RESTORE CONFIRM ── */}
        {step === 'restore-confirm' && (
          <div className={styles.form}>
            <p className={styles.switchText} style={{ marginBottom: '0.5rem' }}>
              This account is scheduled for deletion. Would you like to restore it?
            </p>
            <button type="button" className={styles.primaryBtn} disabled={loading} onClick={() => handleRestoreConfirm(true)}>
              {loading ? <span className={styles.spinner} /> : 'Yes, restore my account'}
            </button>
            <button type="button" className={styles.switchLink} disabled={loading} onClick={() => handleRestoreConfirm(false)} style={{ margin: '0 auto' }}>
              No, keep it deleted
            </button>
          </div>
        )}

        {/* ── FORGOT PASSWORD: email + username ── */}
        {step === 'forgot-email' && (
          <form className={styles.form} onSubmit={handleForgotRequest} noValidate>
            <p className={styles.switchText} style={{ marginBottom: '0.5rem' }}>
              Enter your username and email address to receive a reset code.
            </p>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="forgot-username">Username</label>
              <input id="forgot-username" className={styles.input} type="text" placeholder="Enter your username"
                value={forgotUsername} onChange={e => setForgotUsername(e.target.value.toLowerCase())} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="forgot-email">Email ID</label>
              <input id="forgot-email" className={styles.input} type="email" placeholder="Enter your email address"
                value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} />
            </div>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              {loading ? <span className={styles.spinner} /> : 'Send Reset Code'}
            </button>
            <p className={styles.switchText}>
              <button type="button" className={styles.switchLink} onClick={() => switchTab('login')}>Back to Log In</button>
            </p>
          </form>
        )}

        {/* ── FORGOT PASSWORD: OTP + new password ── */}
        {step === 'forgot-otp' && (
          <form className={styles.form} onSubmit={handleForgotConfirm} noValidate>
            <p className={styles.switchText} style={{ marginBottom: '0.5rem' }}>
              A reset code has been sent to <strong>{forgotEmail}</strong>. Enter it below along with your new password.
            </p>
            <OtpInput value={otp} onChange={setOtp} disabled={loading} />
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="forgot-new-password">New Password</label>
              <PasswordInput id="forgot-new-password" styles={styles} placeholder="At least 8 characters"
                autoComplete="new-password" value={forgotNewPassword}
                onChange={e => setForgotNewPassword(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="forgot-confirm-password">Confirm New Password</label>
              <PasswordInput id="forgot-confirm-password" styles={styles} placeholder="Re-enter new password"
                autoComplete="new-password" value={forgotConfirmPassword}
                onChange={e => setForgotConfirmPassword(e.target.value)} />
            </div>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              {loading ? <span className={styles.spinner} /> : 'Reset Password'}
            </button>
          </form>
        )}

        {/* ── FORGOT PASSWORD: done ── */}
        {step === 'forgot-done' && (
          <div className={styles.form}>
            <p className={styles.switchText} style={{ marginBottom: '0.5rem' }}>
              Your password has been changed. Please log in again.
            </p>
            <button type="button" className={styles.primaryBtn} onClick={() => switchTab('login')}>
              Back to Log In ({doneCountdown}s)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProofSpinnerMock({ styles }) {
  const r = 16, segments = 8;
  const circumference = 2 * Math.PI * r;
  const slot = circumference / segments;
  const dashLen = slot * 0.55;
  const gapLen = circumference - dashLen;
  const segs = [];
  for (let i = 0; i < segments; i++) {
    segs.push(
      <circle key={i} cx="20" cy="20" r={r} fill="none"
        strokeDasharray={`${dashLen} ${gapLen}`}
        strokeDashoffset={-(i * slot)}
        className={styles.stepperTickMock}
        style={{ animationDelay: `${(i * (1.1 / segments)).toFixed(3)}s` }} />
    );
  }
  return <svg viewBox="0 0 40 40" width="20" height="20">{segs}</svg>;
}

// ── Step 3: Review & Confirm — interactive tabs ───────────────────────────────
function ReviewConfirmStep({ styles }) {
  const [activeTab, setActiveTab] = useState('bills');
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={styles.proofStep}>
      <div className={styles.sectionInner}>
        <div className={styles.proofRow}>
          <div className={styles.proofVisualLeft}>
            <div className={`${styles.proofCard} ${styles.proofCardWide}`}>
              <div className={styles.reviewTabsMock}>
                <button type="button"
                  className={`${styles.reviewTabMock} ${activeTab === 'bills' ? styles.reviewTabActiveMock : ''}`}
                  onClick={() => setActiveTab('bills')}>
                  Bills <span className={styles.tabBadgeMock}>1</span>
                </button>
                <button type="button"
                  className={`${styles.reviewTabMock} ${activeTab === 'bank' ? styles.reviewTabActiveMock : ''}`}
                  onClick={() => setActiveTab('bank')}>
                  Bank <span className={styles.tabBadgeMock}>29</span>
                </button>
                <span className={styles.tryClickHintMock}>&larr; Try it, click Bank & Bill Row (below)</span>
              </div>

              {activeTab === 'bills' && (
                <>
                  <div className={styles.filterChipsRowMock}>
                    <span className={styles.filterChipMock}>Mathematical Mismatch <span className={styles.filterChipCountMock}>1</span></span>
                    <span className={`${styles.filterChipMock} ${styles.filterChipActiveMock}`}>Needs Confirmation <span className={styles.filterChipCountMock}>1</span></span>
                  </div>
                  <div className={styles.tableMock}>
                    <div className={styles.tableHeaderMock}>
                      <span>Date</span><span>Invoice #</span><span>Party Name</span><span>GST (Total)</span><span>Ledger</span><span>New?</span>
                    </div>
                    <div
                      className={styles.tableRowMock}
                      onClick={() => setExpanded(e => !e)}
                      style={{cursor: 'pointer'}}
                    >
                      <span>
                        <span className={`${styles.expandArrowMock} ${expanded ? styles.expandArrowOpenMock : ''}`}>&rsaquo;</span>
                        {' '}17.10.2025
                      </span>
                      <span className={styles.confirmFieldMock}>777</span>
                      <span>Omkar Distributors</span>
                      <span className={styles.gstStackMock}>
                        <span>18%</span>
                        <span className={styles.igstTagMock}>CGST+SGST</span>
                      </span>
                      <span className={styles.ledgerSearchMock}>
                        Omkar Dis<span className={styles.ledgerCursorMock}>|</span>
                        <span className={styles.ledgerSuggestMock}>Omkar Distributors</span>
                      </span>
                      <span><span className={styles.newBadgeMock}>New</span></span>
                    </div>
                    {expanded && (
                      <div className={styles.lineItemsMock}>
                        <div className={styles.lineItemsHeaderMock}>
                          <span>Item</span><span>Qty</span><span>Rate</span>
                        </div>
                        <div className={styles.lineItemsRowMock}>
                          <span>LED 43&quot; Haier</span>
                          <span>1</span>
                          <span className={styles.mismatchFieldMock}>8,500</span>
                        </div>
                        <div className={styles.lineItemsRowMock}>
                          <span>Washing Machine LG</span>
                          <span>1</span>
                          <span>13,500</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.warningMock}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#92400e" strokeWidth="1.2"/><line x1="6" y1="3.5" x2="6" y2="6.5" stroke="#92400e" strokeWidth="1.2" strokeLinecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#92400e"/></svg>
                    New ledgers detected. Create them in Tally before importing the CSV.
                  </div>
                  <button className={styles.confirmBtnMock} type="button">Confirm Bills</button>
                </>
              )}

              {activeTab === 'bank' && (
                <>
                  <div className={styles.tableMock}>
                    <div className={styles.bankTableHeaderMock}>
                      <span>Date</span><span>Description</span><span>Ref No</span><span>Debit</span><span>Credit</span><span>Match</span>
                    </div>
                    {[
                      { date: '19.10.22', desc: 'UPI/DR/227714852201', ref: '227714852201', debit: '\u20b9393', credit: '\u2014', match: 'Vinayak Traders', matched: true },
                      { date: '21.12.21', desc: 'UPI/DR/135882741920', ref: '135882741920', debit: '\u20b9478', credit: '\u2014', match: 'No match', matched: false },
                      { date: '22.12.21', desc: 'UPI/DR/135882742188', ref: '135882742188', debit: '\u20b9782', credit: '\u2014', match: 'No match', matched: false },
                    ].map((row, i) => (
                      <div key={i} className={styles.bankTableRowMock}>
                        <span>{row.date}</span>
                        <span className={styles.bankDescMock}>{row.desc}</span>
                        <span>{row.ref}</span>
                        <span>{row.debit}</span>
                        <span>{row.credit}</span>
                        <span className={row.matched ? styles.bankMatchedMock : styles.bankNoMatchMock}>{row.match}</span>
                      </div>
                    ))}
                  </div>
                  <button className={styles.confirmBtnMock} type="button">Confirm Bank Links</button>
                </>
              )}
            </div>
          </div>
          <div className={styles.proofTextRight}>
            <span className={styles.proofStepNum}>03</span>
            <h3 className={styles.proofStepTitle}>Review &amp; Confirm</h3>
            <p className={styles.proofStepBody}>
              We don&rsquo;t want you to search for invisible errors, so we designed our system as an <span className={styles.scriptFont}>Intelligent Accounting Assistant</span>, which checks its own work before you ever need to.
            </p>
            <p className={styles.proofStepBody}>
              We <strong>don&rsquo;t</strong> ask you to trust AI. We ask you to <span className={styles.scriptFont}><strong>Trust Our System.</strong></span>
            </p>
            <p className={styles.proofStepBody}>
              Typing a ledger name tells you instantly whether it already exists in Tally or needs to be created. <strong>No more guesswork, no more duplicate ledgers.</strong>
            </p>
            <p className={styles.proofStepBody}>
              Nothing goes directly into Tally. <em>You stay in full control</em>, reviewing, correcting and confirming before anything leaves the app.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 4: Export — interactive toggles ──────────────────────────────────────
function ExportStep({ styles }) {
  const [isInventory, setIsInventory] = useState(false);
  const [isAggregated, setIsAggregated] = useState(false);
  return (
    <div className={styles.proofStepDark}>
      <div className={styles.sectionInner}>
        <div className={styles.proofRowReverse}>
          <div className={styles.proofTextLeftDark}>
            <span className={styles.proofStepNumLight}>04</span>
            <h3 className={styles.proofStepTitleLight}>Export</h3>
            <p className={styles.proofStepBodyLight}>
              Choose your Bill CSV format. Download Bank CSV separately.
            </p>
            <p className={styles.proofStepBodyLight}>
              <strong>4 types of Bill CSV</strong> - account or inventory, aggregated or not.
              <em> No other tool gives you this flexibility.</em>
            </p>
            <p className={styles.proofStepBodyLight}>
              <span className={styles.scriptFontWhite}>Bas ek click mein!</span>
            </p>
          </div>
          <div className={styles.proofVisualRight}>
            <div className={styles.proofCard}>
              <div className={styles.exportMock}>
                <span className={styles.tryClickHintMockDark}>Try it, flip a toggle &rarr;</span>
                {/* iOS-style Toggle 1 — Inventory Entry */}
                <div className={styles.iosToggleRow}>
                  <div className={styles.iosToggleText}>
                    <span className={styles.iosToggleLabel}>Inventory Entry</span>
                    <span className={styles.iosToggleDesc}>
                      {isInventory ? 'Qty, Unit, Rate included — for stock tracking' : 'Accounting only — no Qty/Unit/Rate'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.iosToggleSwitch} ${isInventory ? styles.iosToggleSwitchOn : ''}`}
                    onClick={() => setIsInventory(p => !p)}
                    aria-label="Toggle Inventory Entry"
                  >
                    <span className={styles.iosToggleThumb} />
                  </button>
                </div>
                {/* iOS-style Toggle 2 — Aggregate by Tax Rate */}
                <div className={styles.iosToggleRow}>
                  <div className={styles.iosToggleText}>
                    <span className={styles.iosToggleLabel}>Aggregate by Tax Rate</span>
                    <span className={styles.iosToggleDesc}>
                      {isAggregated ? 'Items grouped by GST rate — fewer rows' : 'Full item-wise — one row per item'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.iosToggleSwitch} ${isAggregated ? styles.iosToggleSwitchOn : ''}`}
                    onClick={() => setIsAggregated(p => !p)}
                    aria-label="Toggle Aggregate by Tax Rate"
                  >
                    <span className={styles.iosToggleThumb} />
                  </button>
                </div>
                {/* Download buttons — green, compact */}
                <div className={styles.exportBtnRow}>
                  <button className={styles.exportGreenBtn} type="button">
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                      <path d="M7 2v7M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                    Download Bills CSV
                  </button>
                  <button className={styles.exportGreenBtn} type="button">
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                      <path d="M7 2v7M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                    Download Bank CSV
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 5: Grand Proof — full width, interactive CSV tabs ────────────────────
function GrandProofStep({ styles }) {
  const [activeTab, setActiveTab] = useState('bills');
  return (
    <div className={styles.proofStepGrand}>
      <div className={styles.sectionInner}>
        <div className={styles.grandProofHeader}>
          <span className={styles.proofStepNum}>05</span>
          <h3 className={styles.grandProofTitle}>From messy documents to <em className={styles.italicEmphasis}>Tally-ready</em> records.</h3>
          <p className={styles.grandProofSub}>This is your final output which lands in Tally.</p>
        </div>
        {/* Tab switcher */}
        <div className={styles.grandTabRow}>
          <button type="button"
            className={`${styles.grandTab} ${activeTab === 'bills' ? styles.grandTabActive : ''}`}
            onClick={() => setActiveTab('bills')}>
            Bills CSV
          </button>
          <button type="button"
            className={`${styles.grandTab} ${activeTab === 'bank' ? styles.grandTabActive : ''}`}
            onClick={() => setActiveTab('bank')}>
            Bank CSV
          </button>
          <span className={styles.tryClickHintMock}>&larr; Try it, click Bank CSV</span>
        </div>
        {/* Bills CSV spreadsheet */}
        {activeTab === 'bills' && (
          <div className={styles.spreadsheetCard}>
            <div className={styles.spreadsheetBar}>
              <div className={styles.spreadsheetDots}>
                <span style={{background:'#FF5F57'}} /><span style={{background:'#FEBC2E'}} /><span style={{background:'#28C840'}} />
              </div>
              <span className={styles.spreadsheetTitle}>Bills_JOB_514C43D666A44786.csv</span>
            </div>
            {/* <div className={styles.spreadsheetTable}> */}
            <div style={{position: 'relative'}}>
              <div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch'}}>
                <table className={styles.csvTable}>
                  <thead>
                    <tr>
                      {['Date','Voucher Type','Party Name','GSTIN','Taxable','GST Rate','CGST','SGST','IGST','Total','Ledger'].map(h => (
                        <th key={h} className={styles.csvTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['24-06-26', 'Purchase', 'Aaradhya Enterprises', '22ABCDE1234L1Z4', '1,550.00', '0%', '0', '0', '0', '1,550.00', 'Purchase @ 0%'],
                      ['08-04-25', 'Purchase', 'Vinayak Traders', '19BUVFG5656G1ZZ', '303.39', '18%', '0', '0', '54.61', '358.00', 'Vinayak Traders'],
                      ['17-03-24', 'Purchase', 'Anushree Wholesale', '36AALCR3173P1ZO', '294.07', '18%', '0', '0', '52.93', '347.00', 'Anushree Wholesale'],
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 1 ? styles.csvRowAlt : ''}>
                        {row.map((cell, j) => <td key={j} className={styles.csvTd}>{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{position:'absolute',top:0,right:0,width:'50px',height:'100%',background:'linear-gradient(to right, transparent, rgba(255,255,255,0.85))',pointerEvents:'none'}} />
            </div>
          </div>
        )}
        {/* Bank CSV spreadsheet */}
        {activeTab === 'bank' && (
          <div className={styles.spreadsheetCard}>
            <div className={styles.spreadsheetBar}>
              <div className={styles.spreadsheetDots}>
                <span style={{background:'#FF5F57'}} /><span style={{background:'#FEBC2E'}} /><span style={{background:'#28C840'}} />
              </div>
              <span className={styles.spreadsheetTitle}>Bank_JOB_3EE958256AE6466A.csv</span>
            </div>
            {/* <div className={styles.spreadsheetTable}> */}
            <div style={{position: 'relative'}}>
              <div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch'}}>
                <table className={styles.csvTable}>
                  <thead>
                    <tr>
                      {['Voucher Type','Date','Particulars','Amount','Bill Ref','Instrument No','Narration'].map(h => (
                        <th key={h} className={styles.csvTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Payment', '19.10.22', 'COCOBLU RETAIL LIMITED', '₹393.00', 'HYD8-653700', '227714852201', 'UPI/DR/227714852201/COCOBLU'],
                      ['Payment', '21.12.21', 'Cloudtail India Private', '₹478.00', '—', '135882741920', 'UPI/DR/135882741920/CLOUDTAIL'],
                      ['Payment', '11.04.23', 'CACTUS SPICES', '₹616.00', '—', '338851004522', 'UPI/DR/338851004522/CACTUS'],
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 1 ? styles.csvRowAlt : ''}>
                        {row.map((cell, j) => <td key={j} className={styles.csvTd}>{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{position:'absolute',top:0,right:0,width:'50px',height:'100%',background:'linear-gradient(to right, transparent, rgba(255,255,255,0.85))',pointerEvents:'none'}} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Landing Page ─────────────────────────────────────────────────────────
export default function LandingPage() {
  const [showAuth, setShowAuth] = useState(false);

  return (
    <div className={styles.page}>

      {/* Google Fonts — Inter + Dancing Script */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600;700&family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── Navbar ── */}
      <nav className={styles.navbar}>
        <button
          className={styles.navLogo}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          type="button"
        >
          <span className={styles.navLogoIcon}>D</span>
          <span className={styles.navLogoText}>Delefy <span className={styles.navLogoTagline }>Work Better.</span></span>
        </button>
        <button className={styles.navCta} onClick={() => setShowAuth(true)} type="button">
          Log In / Sign Up
        </button>
      </nav>

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <p className={styles.heroEyebrow}>Dear Business owners, CAs &amp; Solo Practitioners,</p>

          <h1 className={styles.heroHeadline}>
            Stop juggling between<br />
            <em className={styles.heroItalic}>five different tools.</em>
          </h1>

          <p className={styles.heroSub}>
            Bill extraction, ledger mapping, bank reconciliation and <br />
            AI error checks. One click. One page.
          </p>

          <div className={styles.heroClaims}>
            <span className={styles.heroClaim}>No prompt engineering</span>
            <span className={styles.heroClaimDot}>·</span>
            <span className={styles.heroClaim}>No copy-paste</span>
            <span className={styles.heroClaimDot}>·</span>
            <span className={styles.heroClaim}>No juggling between five different tools</span>
          </div>

          <div className={styles.heroTime}>
            <span className={styles.heroTimeBefore}>25 min</span>
            <svg width="36" height="16" viewBox="0 0 36 16" fill="none" className={styles.heroArrowSvg}>
              <path d="M2 8h30M24 2l8 6-8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className={styles.heroTimeAfter}>5 min</span>
            <span className={styles.heroTimeLabel}>for 20 bills</span>
          </div>

          <p className={styles.heroScript}><span className={styles.scriptFont}>Bas ek click mein!</span></p>

          <button className={styles.heroBtn} onClick={() => setShowAuth(true)} type="button">
            Try Delefy. It's free
          </button>

          <p className={styles.heroTagline}><strong>A Technology from RiJain</strong></p>
        </div>
      </section>

      {/* ── Section 2: The Impossible Thing ── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.impossibleGrid}>
            <div className={styles.impossibleLeft}>
              <p className={styles.sectionLabel}>The Problem</p>
              <h2 className={styles.sectionHeadline}>
                Why half the evening<br />
                for <em className={styles.playfairItalic}>same books.</em>
              </h2>
              <p className={styles.bodyText}>
                You <strong>spend hours</strong> on bookkeeping which <em>leaves</em> you with <em><strong>no time</strong></em>{' '}
                for your <strong>family,</strong> your <strong>loved ones</strong> and <strong>yourself</strong>.
              </p>
              <p className={styles.bodyText}>
                <strong>Delefy</strong> gives you back <strong>your time</strong> <em>and</em> <strong>your freedom.</strong>
              </p>
              <p className={styles.impossibleClose}>
                <em>Upload Once. Review Once. Download it.</em><br />
                And it's Done.
              </p>
            </div>
            <div className={styles.impossibleRight}>
              <p className={styles.impossibleRightLabel}>One click does:</p>
              <div className={styles.tagGrid}>
                {[
                  'Bill Extraction',
                  'Ledger Mapping (to YOUR Tally setup)',
                  'Bank Reconciliation',
                  'Error Detection',
                  'GST File Prep',
                ].map(tag => (
                  <span key={tag} className={styles.tag}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 3: How it works — Zigzag Proof ── */}
      <section className={styles.sectionProof}>
        <div className={styles.sectionInner} style={{paddingBottom: '0'}}>
          <p className={styles.sectionLabel}>How it works</p>
          <h2 className={styles.sectionHeadline}>See it. <em className={styles.italicEmphasis}>Believe it.</em></h2>
        </div>

        {/* Step 1 — Upload: light bg, visual left, text right */}
        <div className={styles.proofStep} style={{paddingTop: '1rem'}}>
          <div className={styles.sectionInner}>
            <div className={styles.proofRow}>
              <div className={styles.proofVisualLeft}>
                <div className={styles.proofCard}>
                  <div className={styles.billPhotoWrap}>
                    <div className={styles.billPhotoCard}>
                      <div className={styles.receiptHeader}>
                        <strong>OMKAR DISTRIBUTORS</strong>
                        <span>MG Road, Raipur</span>
                        <span>GST: 22ALZPK2823P1ZH</span>
                      </div>
                      <div className={styles.receiptMeta}>
                        <span>Invoice No: 777</span>
                        <span>17/10/25</span>
                      </div>
                      <div className={styles.receiptItems}>
                        {[
                          ['LED 43" Haier',      '1', '28,500'],
                          ['Washing Machine LG', '1', '13,500'],
                        ].map(([item, qty, amt]) => (
                          <div key={item} className={styles.receiptItem}>
                            <span className={styles.receiptItemName}>{item}</span>
                            <span className={styles.receiptItemQty}>&times;{qty}</span>
                            <span className={styles.receiptItemAmt}>&#8377;{amt}</span>
                          </div>
                        ))}
                      </div>
                      <div className={styles.receiptTotal}>
                        <strong>Total</strong>
                        <strong>&#8377;42,000</strong>
                      </div>
                      <div className={styles.receiptMode}>GST Invoice &middot; CGST + SGST</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.proofTextRight}>
                <span className={styles.proofStepNum}>01</span>
                <h3 className={styles.proofStepTitle}>Upload</h3>
                <p className={styles.proofStepBody}>
                  Take a photo or upload bills, receipts, PDFs, bank statements, Master.xml.
                </p>
                <p className={styles.proofStepBody}>
                  <strong>No formatting required.</strong> Messy, crumpled, badly lit. Delefy handles it all.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2 — AI Processing: dark bg, text left, visual right */}
        <div className={styles.proofStepDark}>
          <div className={styles.sectionInner}>
            <div className={styles.proofRowReverse}>
              <div className={styles.proofTextLeftDark}>
                <span className={styles.proofStepNumLight}>02</span>
                <h3 className={styles.proofStepTitleLight}>AI Processing</h3>
                <p className={styles.proofStepBodyLight}>
                  Stacks of bills used to mean hours of typing. Now they mean <span className={styles.scriptFontDark}>minutes</span>. Delefy reads, extracts and structures every bill, so you can get on with the work only a CA can do.
                </p>
                <p className={styles.proofStepBodyLight}>
                  <strong>No manual entry, no re-typing invoice after invoice.</strong> Just clean, structured data, ready for your review, <em>every single time</em>.
                </p>
              </div>
              <div className={styles.proofVisualRight}>
                <div className={styles.proofCard}>
                  <div className={styles.stepperMock}>
                    <div className={styles.stepperRowMock}>
                      <div className={styles.stepperRailMock}>
                        <div className={styles.stepperIconDoneMock}>
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="8" fill="currentColor" />
                            <path d="M4.5 8.2l2.3 2.3L11.5 5.6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div className={styles.stepperLineDoneMock} />
                      </div>
                      <div className={styles.stepperBodyMock}>
                        <span className={styles.stepperTitleDoneMock}>Pre-processing</span>
                        <span className={styles.stepperDetailMock}>Found 3 bills from 3 files.</span>
                      </div>
                    </div>
                    <div className={styles.stepperRowMock}>
                      <div className={styles.stepperRailMock}>
                        <div className={styles.stepperIconActiveMock}>
                          <ProofSpinnerMock styles={styles} />
                        </div>
                      </div>
                      <div className={styles.stepperBodyMock}>
                        <span className={styles.stepperTitleActiveMock}>Extracting Bills</span>
                        <span className={styles.stepperDetailMock}>Extracting your bills&hellip; 1 of 3 done</span>
                        <div className={styles.stepperSubProgressMock}>
                          <div className={styles.stepperSubHeaderMock}>
                            <span>1 / 3 bills</span>
                            <span className={styles.stepperPctMock}>33%</span>
                          </div>
                          <div className={styles.stepperTrackMock}>
                            <div className={styles.stepperBarMock} style={{width: '33%'}} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className={styles.meterNoteMock}>
                    Processing on our AI servers. High traffic may cause slight delays, please keep this page open and do not refresh.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3 — Review & Confirm: light bg, visual left, text right — interactive tabs */}
        <ReviewConfirmStep styles={styles} />

        {/* Step 4 — Export: dark bg, text left, visual right — interactive toggles */}
        <ExportStep styles={styles} />

        {/* Step 5 — Grand Proof: gray bg, full width, interactive CSV tabs */}
        <GrandProofStep styles={styles} />

      </section>

      {/* ── Section 4+5: Bank Recon + Smart Ledger ── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.twoCardGrid}>
            <div className={styles.featureCard}>
              <span className={styles.featureCardLabel}>Bank Reconciliation</span>
              <h3 className={styles.featureCardTitle}>
                Stop <em className={styles.playfairItalicDark}>manually</em> matching transactions.
              </h3>
              <p className={styles.featureCardBody}>
                Upload bank statements. Delefy <strong>automatically matches</strong> bills, transactions and payment records.
                Find mismatches instantly. <strong>Reduce reconciliation workload dramatically.</strong>
              </p>
            </div>
            <div className={styles.featureCard}>
              <span className={styles.featureCardLabel}>Smart Ledger Mapping</span>
              <h3 className={styles.featureCardTitle}>
                Your Tally configuration is <em className={styles.playfairItalicDark}>unique.</em>
              </h3>
              <p className={styles.featureCardBody}>
                Generic tools force generic outputs. Upload your Master.xml,
                Delefy adapts to <strong>YOUR Tally setup.</strong> Custom CSV.
                No manual remapping. No broken imports. <strong>No repetitive corrections.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 6: Performance ── */}
      <section className={styles.sectionGray}>
        <div className={styles.sectionInner}>
          <p className={styles.sectionLabel}>Performance</p>
          <h2 className={styles.sectionHeadline}>
            Built for <em className={styles.playfairItalic}>real-world</em> accuracy.
          </h2>
          <p className={styles.bodyTextMuted} style={{marginBottom: '0.5rem'}}>
            Powered by our custom AI architecture, <strong>RiJain One.</strong>
          </p>
          <p className={styles.bodyTextMuted} style={{marginBottom: '2.5rem'}}>
            Internal benchmarks show:
          </p>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statNumberRow}>
                <span className={styles.statSmall}>up to</span>
                <span className={styles.statNumber}>97%+</span>
              </div>
              <span className={styles.statLabel}>extraction accuracy</span>
              <div className={styles.statDivider} />
              <p className={styles.statDesc}>
                On <strong>clean data</strong> - structured, machine-generated,
                high contrast, consistent layout.
              </p>
              <span className={styles.statTag}>Perfect digital documents</span>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumberRow}>
                <span className={styles.statSmall}>up to</span>
                <span className={styles.statNumber}>95%</span>
              </div>
              <span className={styles.statLabel}>extraction accuracy</span>
              <div className={styles.statDivider} />
              <p className={styles.statDesc}>
                On <em>messy data</em> - captured by camera in
                poor lighting &amp; angles with human-generated layouts.
              </p>
              <span className={styles.statTag}>Messy real-world documents</span>
            </div>
          </div>
          <p className={styles.benchmarkNote}>(Based on internal benchmarks)</p>
        </div>
      </section>

      {/* ── Section 7: Trust ── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.trustBlock}>
            <p className={styles.sectionLabel}>Built for trust</p>
            <h2 className={styles.trustHeadline}>
              AI does the heavy lifting.<br />
              <em className={styles.playfairItalic}>You</em> stay in control.
            </h2>
            <p className={styles.bodyText} style={{textAlign: 'center', maxWidth: '520px'}}>
              Every extracted record passes through the{' '}
              <strong>Review &amp; Confirm Dashboard.</strong>
            </p>
            <div className={styles.trustSteps}>
              {['Check.', 'Edit.', 'Verify.', 'Approve.', 'Then export.'].map((s, i) => (
                <span key={s} className={i === 4 ? styles.trustStepFinal : styles.trustStep}>{s}</span>
              ))}
            </div>
            <p className={styles.trustEmphasis}>
              Nothing happens automatically without your confirmation.<br />
              <strong>Because accounting requires trust.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ── Section 7.5: ICAI Compliance ── */}
      <section className={styles.sectionGray}>
        <div className={styles.sectionInner}>
          <div className={styles.trustBlock}>
            <p className={styles.sectionLabel}>Trust is now compliant</p>
            <h2 className={styles.trustHeadline}>
              Built for Chartered Accountants.<br />
              <em className={styles.playfairItalic}>Not for AI companies.</em>
            </h2>
            <p className={styles.complianceSub}>
              Use AI-powered automation without compromising client confidentiality.
            </p>
            <div className={styles.complianceList}>
              <div className={styles.complianceItem}>
                <span className={styles.complianceCheck}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6.5" stroke="#1A6B3C" strokeWidth="1.3"/>
                    <path d="M4.3 7.2l1.8 1.8 3.6-3.8" stroke="#1A6B3C" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                No permanent document storage.
              </div>
              <div className={styles.complianceItem}>
                <span className={styles.complianceCheck}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6.5" stroke="#1A6B3C" strokeWidth="1.3"/>
                    <path d="M4.3 7.2l1.8 1.8 3.6-3.8" stroke="#1A6B3C" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                No client data used for AI training.
              </div>
              <div className={styles.complianceItem}>
                <span className={styles.complianceCheck}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6.5" stroke="#1A6B3C" strokeWidth="1.3"/>
                    <path d="M4.3 7.2l1.8 1.8 3.6-3.8" stroke="#1A6B3C" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                No public AI APIs handling financial documents.
              </div>
            </div>
            <p className={styles.compliancePunch}>
              ICAI-Compliant by Design.
            </p>
          </div>
        </div>
      </section>

      {/* ── Section 8: Price ── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.trustBlock}>
            <p className={styles.sectionLabel}>Mutual Growth</p>
            <h2 className={styles.trustHeadline}>
               Start free.<br />
              <em className={styles.playfairItalic}>Pay</em> when you need.
            </h2>
            <p className={styles.bodyText} style={{textAlign: 'center', maxWidth: '520px'}}>
              Bringing to you {' '}
              <strong>Plus Bundle</strong><br />
              For those who want to continue when free 20 bill credits end.
            </p>
            <div className={styles.trustSteps}>
              {['₹599', 'for', '200 Bill Credits'].map((s, i) => (
                <span key={s} className={i === 4 ? styles.trustStepFinal : styles.trustStep}>{s}</span>
              ))}
            </div>
            <p className={styles.trustEmphasis}>
              Pay-as-you-go. Zero recurring subscriptions.<br />
              <strong>Credits never expire.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ── Section 8: High Exit ── */}
      <section className={styles.sectionExit}>
        <div className={styles.sectionInner}>
          <h2 className={styles.exitHeadline}>
            Stop <span className={styles.scriptFontExit}>juggling.</span><br />
            Start <span className={styles.exitHeadline2}>Growing.</span>
          </h2>
          <p className={styles.exitSub}>
            If your workflow still depends on multiple tools,<br />
            <strong style={{color: 'rgba(255,255,255,0.85)'}}>Then you must shift to Delefy.</strong><br />
            Because we believe there is always a better way to work<br />
            which must respect your time, money and freedom.
          </p>
          <button className={styles.exitBtn} onClick={() => setShowAuth(true)} type="button">
            Try Delefy. It's free
          </button>
          <p className={styles.exitTagline}><strong>A Technology from RiJain</strong></p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footerFull}>
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <p className={styles.footerBrandName}>Delefy. <span className={styles.footerBrandNameLight}>Work Better.</span></p>
            <p className={styles.footerBrandTagline}>A Technology from RiJain</p>
            <p className={styles.footerEmail}>
              Email: <a href="mailto:support.delefy@rijainone.com">support.delefy@rijainone.com</a>
            </p>
          </div>

          <div className={styles.footerLinks}>
            <div className={styles.footerCol}>
              <p className={styles.footerColLabel}>Company</p>
              <ul className={styles.footerColList}>
                <li><a href="https://rijainone.com/about" target="_blank" rel="noopener noreferrer">About Us</a></li>
                <li><a href="https://rijainone.com/contact" target="_blank" rel="noopener noreferrer">Contact Us</a></li>
                <li><Link to="/legal">Legal</Link></li>
              </ul>
            </div>

            <div className={styles.footerCol}>
              <p className={styles.footerColLabel}>Product</p>
              <ul className={styles.footerColList}>
                <li><button onClick={() => setShowAuth(true)} type="button">Log In / Sign Up</button></li>
                <li><a href="https://rijainone.com/education/delefy" target="_blank" rel="noopener noreferrer">Learn More</a></li>
              </ul>
            </div>

            <div className={styles.footerCol}>
              <p className={styles.footerColLabel}>Follow</p>
              <div className={styles.footerSocial}>
                <a href="https://www.instagram.com/delefy.rijain/" target="_blank" rel="noopener noreferrer" aria-label="Delefy on Instagram">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07zM12 0C8.74 0 8.33.01 7.05.07c-1.28.06-2.15.26-2.91.56a5.9 5.9 0 0 0-2.13 1.39A5.9 5.9 0 0 0 .62 4.14c-.3.76-.5 1.63-.56 2.91C0 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.28.26 2.15.56 2.91.3.79.71 1.46 1.39 2.13.67.67 1.34 1.08 2.13 1.39.76.3 1.63.5 2.91.56C8.33 24 8.74 24 12 24s3.67-.01 4.95-.07c1.28-.06 2.15-.26 2.91-.56.79-.3 1.46-.71 2.13-1.39.67-.67 1.08-1.34 1.39-2.13.3-.76.5-1.63.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.28-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.39-2.13A5.9 5.9 0 0 0 19.86.63c-.76-.3-1.63-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/showcase/delefy-rijain/" target="_blank" rel="noopener noreferrer" aria-label="Delefy on LinkedIn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.14 1.45-2.14 2.94v5.66H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45z" />
                  </svg>
                </a>
                <a href="https://x.com/RiJain_Official" target="_blank" rel="noopener noreferrer" aria-label="RiJain on X">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.9 2H22l-7.5 8.57L23.3 22h-6.9l-5.4-7.06L4.8 22H1.7l8.02-9.17L1 2h7.06l4.88 6.45L18.9 2zm-1.2 18h1.9L7.4 3.9H5.36L17.7 20z" />
                  </svg>
                </a>
                <a href="https://www.youtube.com/@RiJain_Official" target="_blank" rel="noopener noreferrer" aria-label="RiJain on YouTube">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.81 3.02 3.02 0 0 0 2.12 2.14C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.81zM9.6 15.6V8.4l6.4 3.6-6.4 3.6z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <span>© Created by Rishabh Jain</span>
        </div>
      </footer>

      {/* ── Auth Modal ── */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
}